'use client'

import PropTypes from 'prop-types'
import { useEffect, useMemo, useState } from 'react'
import MUIAlert from '@mui/material/Alert'
import MUIStack from '@mui/material/Stack'
import MUITextField from '@mui/material/TextField'
import { CustomDataPageSelect } from '@repo/ui/CustomDataPageSelect'
import { ReactSelectMenu } from '@repo/ui/ReactSelectMenu'
import { TextLinkButton } from '@repo/ui/TextLinkButton'
import { ValidationErrorAlert } from '@repo/ui/ValidationErrorAlert'
import { useCustomDataPageStore } from '@repo/store/useCustomDataPageStore'
import { useFormulaStore } from '@repo/store/useFormulaStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'

// Value shape (controlled): { type, tableName, fieldName, repetitionTableName, repetitionFieldName,
// customDataPageId, customDataFieldId, formulaExpression }. Only the properties relevant to the
// current `type` are populated; the rest stay null. See OS-2351's technical-spec.md.
const EMPTY_VALUE = {
  type: null,
  tableName: null,
  fieldName: null,
  repetitionTableName: null,
  repetitionFieldName: null,
  customDataPageId: null,
  customDataFieldId: null,
  formulaExpression: null
}

const TYPE_OPTIONS = [
  { value: 'Practice', label: 'Practice' },
  { value: 'Client', label: 'Client' },
  { value: 'Matter', label: 'Matter' },
  { value: 'Custom Data', label: 'Custom Data' },
  { value: 'Formula', label: 'Formula' }
]

const CLIENT_FIELD_TABLE_NAMES = ['client', 'linkname', 'cliname', 'cliphone']
const FORMULA_VALIDATION_DEBOUNCE_MS = 500

// Fields from different tables can share the same `name` -- key options by table+name so
// selecting one never gets confused with an identically-named field from another table.
const toFieldOption = (field) => ({
  ...field,
  value: `${field.tableName}:${field.name}`,
  label: field.description
})

const useDebouncedValue = (value, delayMs) => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs)
    return () => clearTimeout(id)
  }, [value, delayMs])
  return debounced
}

export const OspreyFieldSelect = ({
  value = null,
  onChange = (val) => console.log(val),
  workflowTypeId,
  clientNo = null,
  matterNo = null,
  sx,
  size = 'small'
}) => {
  const {
    type,
    tableName,
    fieldName,
    repetitionTableName,
    repetitionFieldName,
    customDataPageId,
    customDataFieldId,
    formulaExpression
  } = value || EMPTY_VALUE

  const { fieldsByTableName, fetchFieldsByTableName, fetchFieldsByTableNames } = useLookupsStore()
  const { customDataPagesByWorktype, fetchSearchCustomDataPageFields } = useCustomDataPageStore()
  const { validateFormula } = useFormulaStore()

  const [customDataFields, setCustomDataFields] = useState([])
  const [formulaValidation, setFormulaValidation] = useState(null)

  useEffect(() => {
    if (type === 'Practice') fetchFieldsByTableName({ tableName: 'practiceinfo' })
    else if (type === 'Matter') fetchFieldsByTableName({ tableName: 'matter' })
    else if (type === 'Client') fetchFieldsByTableNames({ tableNames: CLIENT_FIELD_TABLE_NAMES })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type])

  useEffect(() => {
    if (type !== 'Custom Data' || !customDataPageId) {
      setCustomDataFields([])
      return
    }
    fetchSearchCustomDataPageFields({ customDataPageId }).then((fields) => setCustomDataFields(fields || []))
  }, [type, customDataPageId, fetchSearchCustomDataPageFields])

  const debouncedFormulaExpression = useDebouncedValue(formulaExpression, FORMULA_VALIDATION_DEBOUNCE_MS)

  useEffect(() => {
    if (type !== 'Formula' || !debouncedFormulaExpression?.trim()) {
      setFormulaValidation(null)
      return
    }

    let cancelled = false
    validateFormula({ expression: debouncedFormulaExpression, clientNo, matterNo }).then((result) => {
      if (!cancelled) setFormulaValidation(result)
    })

    return () => {
      cancelled = true
    }
  }, [type, debouncedFormulaExpression, clientNo, matterNo, validateFormula])

  const practiceFieldOptions = useMemo(
    () => (fieldsByTableName?.practiceinfo || []).map(toFieldOption),
    [fieldsByTableName]
  )
  const matterFieldOptions = useMemo(() => (fieldsByTableName?.matter || []).map(toFieldOption), [fieldsByTableName])
  const clientLevel1Options = useMemo(
    () => [...(fieldsByTableName?.client || []), ...(fieldsByTableName?.cliname || [])].map(toFieldOption),
    [fieldsByTableName]
  )
  const clientLevel2Options = useMemo(
    () => [...(fieldsByTableName?.linkname || []), ...(fieldsByTableName?.cliphone || [])].map(toFieldOption),
    [fieldsByTableName]
  )
  const customDataFieldOptions = useMemo(
    () => customDataFields.map((field) => ({ ...field, value: field.customDataFieldId, label: field.description })),
    [customDataFields]
  )

  const selectedCustomDataPage = useMemo(() => {
    if (!customDataPageId) return null
    const pages = customDataPagesByWorktype?.[workflowTypeId] || []
    return pages.find((page) => page.customDataPageId === customDataPageId) || null
  }, [customDataPageId, customDataPagesByWorktype, workflowTypeId])

  const handleTypeChange = (option) => {
    const newType = option?.value || null
    onChange(newType ? { ...EMPTY_VALUE, type: newType } : null)
  }

  const handleFieldChange = (option) => {
    onChange(
      option
        ? { ...EMPTY_VALUE, type, tableName: option.tableName, fieldName: option.name }
        : { ...EMPTY_VALUE, type }
    )
  }

  const handleClientRepetitionChange = (option) => {
    onChange({
      ...value,
      repetitionTableName: option?.tableName || null,
      repetitionFieldName: option?.name || null
    })
  }

  const handleCustomDataPageChange = (page) => {
    onChange({ ...EMPTY_VALUE, type: 'Custom Data', customDataPageId: page?.customDataPageId || null })
  }

  const handleCustomDataFieldChange = (option) => {
    onChange({ ...value, customDataFieldId: option?.value || null })
  }

  const handleFormulaChange = (event) => {
    onChange({ ...(value || EMPTY_VALUE), type: 'Formula', formulaExpression: event.target.value })
  }

  const handleReset = () => onChange(null)

  const selectedType = TYPE_OPTIONS.find((option) => option.value === type) || null
  const practiceOrMatterFieldOptions = type === 'Practice' ? practiceFieldOptions : matterFieldOptions

  return (
    <div data-testid="OspreyFieldSelect" style={{ width: sx?.width || '100%', maxWidth: sx?.maxWidth || '100%' }}>
      <MUIStack gap={2}>
        <MUIStack direction="row" justifyContent="flex-end">
          <TextLinkButton title="Reset" onClick={handleReset} />
        </MUIStack>

        <ReactSelectMenu
          label="Type"
          onChange={handleTypeChange}
          items={TYPE_OPTIONS}
          value={selectedType}
          clearable={false}
          searchable={false}
          name="type"
          size={size}
        />

        {(type === 'Practice' || type === 'Matter') && (
          <ReactSelectMenu
            label="Field"
            onChange={handleFieldChange}
            items={practiceOrMatterFieldOptions}
            value={
              practiceOrMatterFieldOptions.find(
                (option) => option.tableName === tableName && option.name === fieldName
              ) || null
            }
            searchable
            name="field"
            size={size}
          />
        )}

        {type === 'Client' && (
          <>
            <ReactSelectMenu
              label="Field"
              onChange={handleFieldChange}
              items={clientLevel1Options}
              value={
                clientLevel1Options.find((option) => option.tableName === tableName && option.name === fieldName) ||
                null
              }
              searchable
              name="clientField"
              size={size}
            />

            {tableName === 'cliname' && (
              <ReactSelectMenu
                label="Named client / phone number"
                onChange={handleClientRepetitionChange}
                items={clientLevel2Options}
                value={
                  clientLevel2Options.find(
                    (option) => option.tableName === repetitionTableName && option.name === repetitionFieldName
                  ) || null
                }
                searchable
                name="clientFieldRepetition"
                size={size}
              />
            )}
          </>
        )}

        {type === 'Custom Data' && (
          <>
            <CustomDataPageSelect
              workflowTypeId={workflowTypeId}
              value={selectedCustomDataPage}
              onChange={handleCustomDataPageChange}
              size={size}
            />

            <ReactSelectMenu
              label="Field"
              onChange={handleCustomDataFieldChange}
              items={customDataFieldOptions}
              value={customDataFieldOptions.find((option) => option.value === customDataFieldId) || null}
              disabled={!customDataPageId}
              searchable
              name="customDataField"
              size={size}
            />
          </>
        )}

        {type === 'Formula' && (
          <>
            <MUITextField
              data-testid="OspreyFieldSelectFormula"
              multiline
              rows={3}
              fullWidth
              label="Formula"
              placeholder="e.g. {!CALCULATION.ADDRESS}"
              value={formulaExpression || ''}
              onChange={handleFormulaChange}
              sx={{ bgcolor: 'common.white' }}
            />

            {formulaValidation?.isValid && (
              <MUIAlert data-testid="OspreyFieldSelectFormulaValid" severity="success">
                Formula is valid
              </MUIAlert>
            )}

            {formulaValidation && !formulaValidation.isValid && (
              <ValidationErrorAlert errorCount={1} validationMessage={formulaValidation.message} />
            )}
          </>
        )}
      </MUIStack>
    </div>
  )
}

OspreyFieldSelect.propTypes = {
  value: PropTypes.object,
  onChange: PropTypes.func,
  workflowTypeId: PropTypes.string,
  clientNo: PropTypes.string,
  matterNo: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  sx: PropTypes.object,
  size: PropTypes.string
}
